import { useState, useMemo } from "react";
import { Calendar } from "./ui/calendar";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";

const MOOD_EMOJIS = {
  very_happy: "😄",
  happy: "🙂",
  neutral: "😐",
  sad: "😔",
  very_sad: "😢",
  angry: "😡",
  anxious: "😰",
  tired: "😴",
  in_love: "😍",
  sick: "🤒",
  surprised: "😲",
  relaxed: "😌",
  sporty: "💪",
  forgiven: "🙏",
};

const MOOD_COLORS = {
  very_happy: "bg-green-500/30 border-green-500/50",
  happy: "bg-lime-500/30 border-lime-500/50",
  neutral: "bg-yellow-500/30 border-yellow-500/50",
  sad: "bg-blue-500/30 border-blue-500/50",
  very_sad: "bg-indigo-500/30 border-indigo-500/50",
  angry: "bg-red-500/30 border-red-500/50",
  anxious: "bg-orange-500/30 border-orange-500/50",
  tired: "bg-slate-500/30 border-slate-500/50",
  in_love: "bg-pink-500/30 border-pink-500/50",
  sick: "bg-emerald-500/30 border-emerald-500/50",
  surprised: "bg-amber-500/30 border-amber-500/50",
  relaxed: "bg-cyan-500/30 border-cyan-500/50",
  sporty: "bg-orange-500/30 border-orange-500/50",
  forgiven: "bg-violet-500/30 border-violet-500/50",
};

export const MoodCalendar = ({ moods, loading }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Create a map of dates to moods for quick lookup
  const moodMap = useMemo(() => {
    const map = {};
    moods.forEach((mood) => {
      map[mood.date] = mood;
    });
    return map;
  }, [moods]);

  // Get mood for selected date
  const selectedMood = selectedDate ? moodMap[format(selectedDate, "yyyy-MM-dd")] : null;

  // Custom day content renderer
  const renderDay = (day) => {
    const dateStr = format(day, "yyyy-MM-dd");
    const mood = moodMap[dateStr];
    
    if (mood) {
      return (
        <div className="relative w-full h-full flex items-center justify-center">
          <span className="text-lg">{MOOD_EMOJIS[mood.mood]}</span>
        </div>
      );
    }
    return <span>{format(day, "d")}</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-neon-cyan/30 border-t-neon-cyan rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Calendar */}
      <div className="lg:col-span-2">
        <div className="bg-black/30 rounded-xl p-4 border border-white/5">
          <Calendar
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate}
            month={currentMonth}
            onMonthChange={setCurrentMonth}
            locale={es}
            data-testid="mood-calendar"
            className="w-full"
            classNames={{
              months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
              month: "space-y-4 w-full",
              caption: "flex justify-center pt-1 relative items-center",
              caption_label: "font-orbitron text-lg tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-neon-cyan to-neon-purple",
              nav: "space-x-1 flex items-center",
              nav_button: "h-8 w-8 bg-transparent p-0 opacity-70 hover:opacity-100 text-neon-cyan hover:bg-neon-cyan/10 rounded-lg transition-all",
              nav_button_previous: "absolute left-1",
              nav_button_next: "absolute right-1",
              table: "w-full border-collapse space-y-1",
              head_row: "flex w-full",
              head_cell: "text-slate-500 rounded-md w-full font-rajdhani font-semibold text-sm uppercase",
              row: "flex w-full mt-2",
              cell: "relative w-full p-0 text-center text-sm focus-within:relative focus-within:z-20",
              day: "h-10 w-full p-0 font-normal text-slate-300 hover:bg-neon-cyan/10 rounded-lg transition-colors flex items-center justify-center",
              day_selected: "bg-neon-cyan/20 text-white border border-neon-cyan/50",
              day_today: "bg-neon-purple/20 text-white border border-neon-purple/50",
              day_outside: "text-slate-600 opacity-50",
              day_disabled: "text-slate-600 opacity-30",
              day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
              day_hidden: "invisible",
            }}
            components={{
              IconLeft: () => <ChevronLeft className="h-4 w-4" />,
              IconRight: () => <ChevronRight className="h-4 w-4" />,
              DayContent: ({ date }) => renderDay(date),
            }}
            modifiers={{
              hasMood: (date) => moodMap[format(date, "yyyy-MM-dd")] !== undefined,
            }}
            modifiersClassNames={{
              hasMood: "!bg-neon-cyan/10 border border-neon-cyan/30",
            }}
          />
        </div>
      </div>

      {/* Selected Day Details */}
      <div>
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          className="bg-black/30 rounded-xl p-6 border border-white/5 h-full"
        >
          <h3 className="font-rajdhani font-bold text-lg uppercase tracking-wide text-slate-300 mb-4">
            Detalles del Día
          </h3>
          
          {selectedDate ? (
            <div className="space-y-4">
              <p className="font-orbitron text-neon-cyan">
                {format(selectedDate, "EEEE, d 'de' MMMM yyyy", { locale: es })}
              </p>
              
              {selectedMood ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className={`p-4 rounded-xl border ${MOOD_COLORS[selectedMood.mood]}`}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-4xl">{MOOD_EMOJIS[selectedMood.mood]}</span>
                    <div>
                      <p className="font-rajdhani font-bold text-white capitalize">
                        {selectedMood.mood.replace("_", " ")}
                      </p>
                      <p className="text-xs text-slate-400">
                        {format(parseISO(selectedMood.timestamp), "HH:mm", { locale: es })}
                      </p>
                    </div>
                  </div>
                  {selectedMood.note && (
                    <p className="text-sm text-slate-300 font-inter leading-relaxed border-t border-white/10 pt-3 mt-3">
                      "{selectedMood.note}"
                    </p>
                  )}
                </motion.div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-slate-500 font-inter">
                    No hay registro para este día
                  </p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-slate-500 font-inter">
                Selecciona un día para ver los detalles
              </p>
            </div>
          )}
        </motion.div>

        {/* Legend */}
        <div className="mt-4 bg-black/30 rounded-xl p-4 border border-white/5">
          <h4 className="font-rajdhani font-semibold text-sm uppercase tracking-wide text-slate-400 mb-3">
            Leyenda
          </h4>
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(MOOD_EMOJIS).map(([key, emoji]) => (
              <div key={key} className="flex items-center gap-2 text-xs">
                <span>{emoji}</span>
                <span className="text-slate-400 capitalize">{key.replace("_", " ")}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
